import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';
import type { Issue, IssueStatus } from '@/types/issue';
import {
  createIssue,
  updateIssue,
  deleteIssue,
  archiveIssue,
  updateIssueStatus,
  addComment,
  subscribeToIssues,
  getArchivedIssues,
} from '@/lib/firebase/issueUtils';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@/lib/firebase/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

export const useIssues = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [archivedIssues, setArchivedIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Subscribe to active issues
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToIssues(
      (updatedIssues) => {
        setIssues(updatedIssues);
        setLoading(false);
      },
      (error) => {
        setError(error);
        setLoading(false);
        toast({
          title: "Error",
          description: "Failed to load issues. Please try again.",
          variant: "destructive",
        });
      }
    );

    return () => unsubscribe();
  }, [user, toast]);

  // Fetch archived issues
  const fetchArchivedIssues = useCallback(async () => {
    if (!user) return;

    try {
      const archived = await getArchivedIssues();
      setArchivedIssues(archived);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load archived issues. Please try again.",
        variant: "destructive",
      });
    }
  }, [user, toast]);

  // Load archived issues on mount
  useEffect(() => {
    fetchArchivedIssues();
  }, [fetchArchivedIssues]);

  // Create a new issue
  const handleCreateIssue = useCallback(async (
    data: Omit<Issue, 'id' | 'createdAt' | 'updatedAt'>,
    attachments?: File[]
  ) => {
    if (!user) return;

    try {
      const newIssue = await createIssue(data, attachments);

      toast({
        title: "Success",
        description: "Issue created successfully",
      });

      return newIssue;
    } catch (error) {
      console.error('Error creating issue:', error);
      toast({
        title: "Error",
        description: "Failed to create issue",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, toast]);

  // Update an issue
  const handleUpdateIssue = useCallback(async (
    issueId: string,
    updates: Partial<Issue>
  ) => {
    if (!user) return;

    try {
      // Get current issue first
      const issueRef = doc(db, 'issues', issueId);
      const currentIssue = await getDoc(issueRef);
      
      if (!currentIssue.exists()) {
        throw new Error('Issue not found');
      }

      const currentData = currentIssue.data();

      await updateIssue(issueId, {
        ...updates,
        // Ensure we preserve existing activity
        activity: updates.activity || currentData.activity || [],
        updatedBy: {
          id: user.uid,
          name: user.displayName || 'Unknown User',
          email: user.email || '',
          photoURL: user.photoURL || undefined,
        },
      });

      toast({
        title: "Success",
        description: "Issue updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update issue",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, toast]);

  // Delete an issue
  const handleDeleteIssue = useCallback(async (issueId: string) => {
    if (!user) return;

    try {
      // First get the issue
      const issueRef = doc(db, 'issues', issueId);
      const issueDoc = await getDoc(issueRef);
      
      if (!issueDoc.exists()) {
        throw new Error('Issue not found');
      }

      const issue = {
        id: issueDoc.id,
        ...issueDoc.data()
      } as Issue;

      // Now delete the issue with all its attachments
      await deleteIssue(issue);
      
      toast({
        title: "Success",
        description: "Issue deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting issue:', error);
      toast({
        title: "Error",
        description: "Failed to delete issue",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, toast]);

  // Archive an issue
  const handleArchiveIssue = useCallback(async (issue: Issue) => {
    if (!user) return;

    try {
      await archiveIssue(issue, user.uid);
      toast({
        title: "Success",
        description: "Issue archived successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to archive issue",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, toast]);

  // Update issue status
  const handleStatusChange = useCallback(async (
    issue: Issue,
    newStatus: IssueStatus
  ) => {
    if (!user) return;

    try {
      await updateIssueStatus(issue, newStatus, user.uid);
      toast({
        title: "Success",
        description: `Status changed to ${newStatus}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, toast]);

  // Add a comment
  const handleAddComment = useCallback(async (
    issueId: string,
    commentData: {
      content: string;
      createdAt: string;
      createdBy: {
        id: string;
        name: string;
        email: string;
        photoURL?: string;
      };
      parentId?: string | null;
      mentions?: Reference[];
      references?: Reference[];
    }
  ) => {
    if (!user) return;

    try {
      const issueRef = doc(db, 'issues', issueId);
      const issueDoc = await getDoc(issueRef);
      
      if (!issueDoc.exists()) {
        throw new Error('Issue not found');
      }

      const newComment = {
        id: crypto.randomUUID(),
        ...commentData,
        parentId: commentData.parentId || null, // Ensure parentId is explicitly null for top-level comments
      };

      // Add the new comment to the comments array
      await updateDoc(issueRef, {
        comments: arrayUnion(newComment),
        updatedAt: new Date().toISOString(),
        updatedBy: {
          id: user.uid,
          name: user.displayName || 'Unknown User',
          email: user.email || '',
          photoURL: user.photoURL || undefined,
        },
      });

      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, toast]);

  // Fetch available users for mentions
  const getAvailableUsers = useCallback(async () => {
    if (!user) return [];

    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      return usersSnapshot.docs.map((userDoc) => {
        const userData = userDoc.data();
        
        // Return all users, with fallback for missing data
        return {
          id: userDoc.id,
          display: userData.displayName || userData.email || 'Unknown User',
          type: 'user' as const,
          email: userData.email || '',
          photoURL: userData.photoURL || null, // Make it null if missing
        };
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }, [user]);

  return {
    issues,
    archivedIssues,
    loading,
    error,
    createIssue: handleCreateIssue,
    updateIssue: handleUpdateIssue,
    deleteIssue: handleDeleteIssue,
    archiveIssue: handleArchiveIssue,
    updateStatus: handleStatusChange,
    addComment: handleAddComment,
    refreshArchived: fetchArchivedIssues,
    getAvailableUsers,
  };
}; 